import { useState } from 'react';

const industries = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Marketing',
  'Engineering',
  'Design',
  'Sales',
  'Operations',
  'Legal',
];
const salaryRanges = [
  '$0–$30k',
  '$30k–$60k',
  '$60k–$90k',
  '$90k–$120k',
  '$120k–$150k',
  '$150k+',
];
const jobTypes = [
  'Full-time',
  'Part-time',
  'Contract',
  'Freelance',
  'Internship',
  'Remote',
];
const experienceLevels = [
  'Internship',
  'Entry Level',
  'Mid Level',
  'Senior Level',
  'Lead / Manager',
  'Director / VP',
  'C-Level / Executive',
];
const workModes = ['On-site', 'Remote', 'Hybrid'];
const educationLevels = [
  'High School / GED',
  "Associate's Degree",
  "Bachelor's Degree",
  "Master's Degree",
  'PhD / Doctorate',
  'Professional Certification',
  'No Requirement',
];
const benefitsList = [
  'Health Insurance',
  'Dental & Vision',
  '401(k) / Pension',
  'Paid Time Off',
  'Remote Work',
  'Stock Options / Equity',
  'Learning & Development Budget',
  'Parental Leave',
  'Gym / Wellness Allowance',
  'Flexible Hours',
];

function Label({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-inter mb-2 text-black font-semibold">
      {children} {required && <span className="text-red-500 font-bold">*</span>}
    </label>
  );
}

function Input({ className = '', ...props }) {
  return (
    <input
      className={`w-full px-4 py-3 border border-[#F0F0F0] rounded-lg bg-black/[0.004] text-[11px] outline-none text-[#272727] placeholder-gray-400 transition-colors ${className}`}
      {...props}
    />
  );
}

function Textarea({ className = '', ...props }) {
  return (
    <textarea
      className={`w-full px-4 py-3 border border-[#F0F0F0] rounded-lg bg-black/[0.004] text-[11px] outline-none text-[#272727] placeholder-gray-400 resize-vertical ${className}`}
      {...props}
    />
  );
}

function Select({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(!open);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 py-3 border border-[#F0F0F0] rounded-lg text-sm bg-white flex justify-between items-center"
      >
        <span className="text-[#272727] text-[11px]">
          {value || placeholder}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" stroke="#9ca3af" strokeWidth="2" fill="none" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-gray-50 border border-[#F0F0F0] rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`px-4 py-2 text-xs cursor-pointer hover:bg-violet-50 ${
                value === opt ? 'bg-violet-50 text-violet-700' : ''
              }`}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BenefitsSelector({
  value,
  onChange,
}: {
  value: string[];
  onChange: (val: string[]) => void;
}) {
  const toggle = (b: string) =>
    onChange(value.includes(b) ? value.filter((x) => x !== b) : [...value, b]);
  return (
    <div className="flex flex-wrap gap-2">
      {benefitsList.map((b) => (
        <button
          key={b}
          type="button"
          onClick={() => toggle(b)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
            value.includes(b)
              ? 'bg-violet-100 border-violet-600 text-violet-700'
              : 'bg-white  border-[#D4D4D4] border-[0.5px] text-gray-500 hover:border-violet-400 hover:text-violet-600'
          }`}
        >
          {b}
        </button>
      ))}
    </div>
  );
}

// ------------------ Updated RequirementTab ------------------
type RequirementTabProps = {
  data: any;
  setData: (val: any) => void;
};

export default function RequirementTab({ data, setData }: RequirementTabProps) {
  const set = (k: string, v: any) => setData({ ...data, [k]: v });

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Job Title</Label>
          <Input
            value={data.jobTitle || ''}
            onChange={(e) => set('jobTitle', e.target.value)}
            placeholder="e.g. Senior Product Designer"
          />
        </div>
        <div>
          <Label required>Industry</Label>
          <Select
            options={industries}
            value={data.industry || ''}
            onChange={(v) => set('industry', v)}
            placeholder="Select..."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Job Type</Label>
          <Select
            options={jobTypes}
            value={data.jobType || ''}
            onChange={(v) => set('jobType', v)}
            placeholder="Select..."
          />
        </div>
        <div>
          <Label required>Work Mode</Label>
          <Select
            options={workModes}
            value={data.workMode || ''}
            onChange={(v) => set('workMode', v)}
            placeholder="Select..."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label required>Job Location</Label>
          <Input
            value={data.location || ''}
            onChange={(e) => set('location', e.target.value)}
            placeholder="e.g. New York, NY or Worldwide"
          />
        </div>
        <div>
          <Label required>Experience Level</Label>
          <Select
            options={experienceLevels}
            value={data.experienceLevel || ''}
            onChange={(v) => set('experienceLevel', v)}
            placeholder="Select..."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Min. Years of Experience</Label>
          <Input
            type="number"
            min={0}
            value={data.minExp || ''}
            onChange={(e) => set('minExp', e.target.value)}
            placeholder="e.g. 2"
          />
        </div>
        <div>
          <Label>Max. Years of Experience</Label>
          <Input
            type="number"
            min={0}
            value={data.maxExp || ''}
            onChange={(e) => set('maxExp', e.target.value)}
            placeholder="e.g. 5"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Minimum Education Required</Label>
          <Select
            options={educationLevels}
            value={data.education || ''}
            onChange={(v) => set('education', v)}
            placeholder="Select..."
          />
        </div>
        <div>
          <Label required>Salary Range</Label>
          <Select
            options={salaryRanges}
            value={data.salaryRange || ''}
            onChange={(v) => set('salaryRange', v)}
            placeholder="Select..."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Number of Openings</Label>
          <Input
            type="number"
            min={1}
            value={data.openings || ''}
            onChange={(e) => set('openings', e.target.value)}
            placeholder="e.g. 3"
          />
        </div>
        <div>
          <Label>Application Deadline</Label>
          <Input
            type="date"
            value={data.deadline ? data.deadline.split('T')[0] : ''}
            onChange={(e) => set('deadline', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label required>Recruiter Email</Label>
        <Input
          type="email"
          value={data.email || ''}
          onChange={(e) => set('email', e.target.value)}
          placeholder="hr@company.com"
        />
      </div>

      <div>
        <Label>Required Skills / Technologies</Label>
        <Textarea
          className="min-h-20"
          value={data.skills || ''}
          onChange={(e) => set('skills', e.target.value)}
          placeholder="e.g. React, Node.js, Python, AWS..."
        />
      </div>

      <div>
        <Label>Benefits & Perks</Label>
        <BenefitsSelector
          value={data.benefits || []}
          onChange={(v) => set('benefits', v)}
        />
      </div>

      <div>
        <Label required>Job Description</Label>
        <Textarea
          className="min-h-28"
          value={data.jobDesc || ''}
          onChange={(e) => set('jobDesc', e.target.value)}
          placeholder="Describe the role, responsibilities..."
        />
      </div>
    </div>
  );
}
